import { GP_TYPE } from '@lmc/ts-common'
import { getCleanRoute } from 'hooks/useRouteMatch'
import { useRouter } from 'hooks/useRouter'
import useFeatureFlags from 'hooks/useFeatureFlags'

const profileRoutes = [
  '/residents/[id]/profile',
  '/residents/[id]/needs',
  '/residents/[id]/important-people',
  '/residents/[id]/about-me',
  '/residents/[id]/future-plans',
  '/residents/[id]/medications',
  '/residents/[id]/care-package-and-patterns'
]

const chartsRoutes = [
  '/residents/[id]/charts',
  '/residents/[id]/risk-assessment-scores'
]

const carePlanRoutes = [
  '/residents/[id]/care-plans',
  '/residents/[id]/initial-assessments',
  '/residents/[id]/risk-assessments',
  '/residents/[id]/goals'
]

const emarRoutes = ['/residents/[id]/emar', '/residents/[id]/emar/stock']

const gpConnetRoutes = [
  `/residents/[id]/gp-connect/${GP_TYPE.ADM}`,
  `/residents/[id]/gp-connect/${GP_TYPE.ALL}`,
  `/residents/[id]/gp-connect/${GP_TYPE.CLI}`,
  `/residents/[id]/gp-connect/${GP_TYPE.ENC}`,
  `/residents/[id]/gp-connect/${GP_TYPE.IMM}`,
  `/residents/[id]/gp-connect/${GP_TYPE.MED}`,
  `/residents/[id]/gp-connect/${GP_TYPE.OBS}`,
  `/residents/[id]/gp-connect/${GP_TYPE.PRB}`,
  `/residents/[id]/gp-connect/${GP_TYPE.REF}`,
  `/residents/[id]/gp-connect/${GP_TYPE.SUM}`
]

const getHealthRecordsRoutes = (
  communicationRecordsEnabled: boolean,
  proceduresEnabled: boolean = false
) => {
  const routes = []

  if (communicationRecordsEnabled) {
    routes.push('/residents/[id]/communication-records')
  }

  if (proceduresEnabled) {
    routes.push('/residents/[id]/procedures')
  }

  return routes
}

export const useResidentsSidebarNavigation = () => {
  const router = useRouter()

  const { location } = router

  const flags = useFeatureFlags({
    flagNames: ['communication_records', 'procedures']
  })?.data

  const communicationRecordsEnabled = flags?.communication_records?.enabled
  const proceduresEnabled = flags?.procedures?.enabled

  const healthRecordsRoutes = getHealthRecordsRoutes(
    communicationRecordsEnabled || false,
    proceduresEnabled
  )

  const cleanRoute = getCleanRoute(location.pathname, router.params)

  const getLastUrlKey = () => router.location.pathname.split('/').slice(-1)[0]

  const getDefaultOpenKey = () => {
    if (profileRoutes.includes(cleanRoute)) {
      return 'profile-route'
    }

    if (chartsRoutes.includes(cleanRoute)) {
      return 'charts-route'
    }

    if (carePlanRoutes.includes(cleanRoute)) {
      return 'care-plans-route'
    }

    if (emarRoutes.includes(cleanRoute)) {
      return 'emar-route'
    }

    if (gpConnetRoutes.includes(cleanRoute)) {
      return 'gp-connect-route'
    }

    if (healthRecordsRoutes.includes(cleanRoute)) {
      return 'health-records-route'
    }

    return ''
  }

  const defaultOpenKey = getDefaultOpenKey()

  const lastUrlKey = getLastUrlKey()

  const getSelectedKey = () => {
    switch (defaultOpenKey) {
      case 'profile-route':
      case 'care-plans-route':
      case 'charts-route':
        return lastUrlKey
      case 'emar-route':
        if (lastUrlKey === 'stock') {
          return 'emar/stock'
        }
        return lastUrlKey
      case 'gp-connect-route':
        return `gp-connect/${lastUrlKey}`
      default:
        return lastUrlKey
    }
  }

  const defaultSelectedKey = getSelectedKey()

  return { defaultOpenKey, defaultSelectedKey }
}
